import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE } from "@/util/constants";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Resolves the current builder product ("form" | "survey" | "quiz") to its
 * translated noun, exposed as vue-i18n interpolation params for copy that is
 * shared across products, e.g.
 * `t("form.scheduleVisibility.title", productParams)` →
 * "Schedule form|survey|quiz visibility". Both a lowercase (`product`) and a
 * capitalized (`Product`) noun are provided because capitalization rules
 * differ mid-sentence vs sentence-start (and per locale).
 */
export const useProductNoun = () => {
  const store = useAppStore();
  const { t } = useI18n();

  const productKey = computed<string>(() => {
    const scopeType = store.linkedWorkflowsScope?.productType;
    if (scopeType === "forms") return PRODUCT_TYPE.FORM;
    if (scopeType === "surveys") return PRODUCT_TYPE.SURVEY;
    if (scopeType === "quizzes") return PRODUCT_TYPE.QUIZ;
    // Modal mode has no workflow scope, but its Share pane still needs the asset noun.
    if (store.formId) return PRODUCT_TYPE.FORM;
    if (store.surveyId) return PRODUCT_TYPE.SURVEY;
    if (store.quizId) return PRODUCT_TYPE.QUIZ;
    if (store.product === PRODUCT_TYPE.SURVEY || store.product === PRODUCT_TYPE.QUIZ) {
      return store.product;
    }
    return PRODUCT_TYPE.FORM;
  });

  // Static i18n keys (no dynamic-key lint violation).
  const productParams = computed(() => {
    if (productKey.value === PRODUCT_TYPE.SURVEY) {
      return {
        product: t("common.productNoun.survey"),
        Product: t("common.productNounCapitalized.survey"),
      };
    }
    if (productKey.value === PRODUCT_TYPE.QUIZ) {
      return {
        product: t("common.productNoun.quiz"),
        Product: t("common.productNounCapitalized.quiz"),
      };
    }
    return {
      product: t("common.productNoun.form"),
      Product: t("common.productNounCapitalized.form"),
    };
  });

  const assetParams = computed(() => ({ asset: productParams.value.product }));

  return { productKey, productParams, assetParams };
};
